import React, {useState, useEffect} from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    Button,
    CircularProgress,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import {HighlightOff} from "@mui/icons-material";
import EnhancedSelect from "../../../../components/EnhancedSelect";
import EnhancedSnackbar from "../../../../components/EnhancedSnackbar";
import {utilCheckSupplierSync, utilRequestSupplierSync} from "../../../../utils/utils";
import moment from "moment";
import { Alert } from '@mui/material';
import { DialogHeader } from "../../../../theme";

interface AvailableSupplier {
    Id: string | number;
    CompanyName: string;
    [key: string]: unknown;
}

interface RefreshSupplierDataModalProps {
    open: boolean;
    onClose: () => void;
    availableSuppliers: AvailableSupplier[];
    onSuccess: (selectedSupplier?: { label: string; value: string | number; [key: string]: unknown }) => void;
    type?: 'product' | 'inventory' | string;
}

function RefreshSupplierDataModal(props: RefreshSupplierDataModalProps) {
    const {open, onClose, availableSuppliers, onSuccess, type = 'product'} = props;
    const theme = useTheme();
    
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [jobRunning, setJobRunning] = useState(false);
    const [syncNotConfigured, setSyncNotConfigured] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');

    // One-shot timers scheduled by handleTriggerSync; tracked so we can clear them if the
    // modal unmounts before they fire (avoids setState-after-unmount, and stops a leaked
    // timer from bleeding into a later unit test).
    const triggerTimeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);

    // Transform suppliers to options for EnhancedSelect
    const supplierOptions = availableSuppliers?.map(supplier => ({
        value: supplier.Id || supplier.id,
        label: supplier.Name || supplier.name || supplier.CompanyName,
        ...supplier
    })) || [];

    const checkSyncStatus = React.useCallback(async (supplierOverride = null) => {
        const supplierToCheck = supplierOverride || selectedSupplier;
        if (!supplierToCheck) return;
        
        try {
            const result = await utilCheckSupplierSync(supplierToCheck.value, type);
            if (result.success) {
                setSyncStatus(result.data);
                
                // Check if sync is configured for this partner (If Schedule is null, then it is not configured)
                if (!result.data?.Schedule) {
                    setSyncNotConfigured(true);
                    setJobRunning(false);
                } else {
                    setSyncNotConfigured(false);
                    // Job is running if LastEnd is null
                    setJobRunning(!result.data?.LastEnd);
                }
            }
        } catch (error) {
            console.error('Error checking sync status:', error);
        }
    }, [selectedSupplier, type]);

    // Poll sync status when modal is open and supplier is selected
    useEffect(() => {
        let pollInterval;
        
        if (open && selectedSupplier && !jobRunning) {
            checkSyncStatus();
            
            pollInterval = setInterval(() => {
                checkSyncStatus();
            }, 5000);
        }
        
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [open, selectedSupplier, jobRunning, checkSyncStatus]);

    // On unmount, cancel any pending one-shot timers scheduled by handleTriggerSync.
    useEffect(() => {
        return () => {
            triggerTimeoutsRef.current.forEach(clearTimeout);
            triggerTimeoutsRef.current = [];
        };
    }, []);

    const handleSupplierChange = (supplier) => {
        setSelectedSupplier(supplier);
        setSyncStatus(null);
        setJobRunning(false);
        setSyncNotConfigured(false);
        
        if (supplier) {
            // Pass supplier directly to avoid closure issue
            checkSyncStatus(supplier);
        }
    };

    const handleTriggerSync = async () => {
        if (!selectedSupplier) return;
        
        setLoading(true);
        try {
            const result = await utilRequestSupplierSync(selectedSupplier.value, type);
            if (result.success) {
                setJobRunning(true);
                
                const jobType = type === 'inventory' ? 'inventory' : 'products';
                const message = `The fetch ${jobType} job has been queued for ${selectedSupplier.label}. Please wait for it to complete. You may need to refresh your browser once the job finishes.`;
                setSnackbarMessage(message);
                setSnackbarOpen(true);
                
                if (onSuccess) {
                    onSuccess(selectedSupplier);
                }
                
                triggerTimeoutsRef.current.push(setTimeout(() => {
                    handleClose();
                }, 500));

                triggerTimeoutsRef.current.push(setTimeout(() => {
                    checkSyncStatus();
                }, 1000));
            } else {
                console.error('Failed to trigger sync:', result.error);
            }
        } catch (error) {
            console.error('Error triggering sync:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedSupplier(null);
        setSyncStatus(null);
        setJobRunning(false);
        setSyncNotConfigured(false);
        onClose();
    };

    const getLastUpdatedText = () => {
        if (!syncStatus) return "";
        
        // Check if sync is configured
        if (!syncStatus.Schedule) {
            return "Sync not configured for this partner";
        }
        
        const lastUpdate = syncStatus.LastEnd;
        if (lastUpdate) {
            return `Last Updated: ${moment(lastUpdate).format("MM/DD/YYYY hh:mm:ss A")}`;
        }
        
        // If LastEnd is null, we show "Fetching in progress..." in the UI instead
        if (!lastUpdate && jobRunning) {
            return "";
        }
        
        return "Last Updated: Never";
    };

    const isConfirmDisabled = !selectedSupplier || jobRunning || loading || syncNotConfigured;

    return (
        <>
            <Dialog
                open={open}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick') handleClose();
                }}
                maxWidth="md"
                fullWidth
            >
            {/* Success bar */}
            <div style={{
                height: "10px",
                width: "100%",
                background: "#22b36e",
                transition: "background 0.5s ease"
            }}/>

            <DialogTitle style={{
                padding: "8px 12px"
            }}>
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item container style={{flex: 1, flexDirection: "column"}}>
                        <DialogHeader>
                            Refresh Partner Data
                        </DialogHeader>
                        <Typography style={{fontSize: "14px", color: theme.palette.text.secondary}}>
                            Partner data is automatically updated daily. Data can be manually refreshed per partner here.
                        </Typography>
                    </Grid>

                    <IconButton onClick={handleClose} size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>

            <DialogContent style={{padding: "0 12px 12px 12px"}}>
                <Grid container spacing={2}>
                    {/* Supplier Selection */}
                    <Grid item size={12}>
                        <EnhancedSelect
                            label="Select Supplier"
                            placeholder="Choose a supplier..."
                            options={supplierOptions}
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            required
                        />
                    </Grid>

                    {/* Last Updated Info */}
                    {selectedSupplier && (
                        <Grid item size={12}>
                            <Typography style={{
                                fontSize: "14px",
                                color: theme.palette.text.secondary
                            }}>
                                {syncNotConfigured ? (
                                    <span style={{color: "#f44336"}}>
                                        Sync not configured for this partner
                                    </span>
                                ) : jobRunning ? (
                                    <span style={{color: "#ff9800", display: "flex", alignItems: "center"}}>
                                        <CircularProgress size={16} style={{marginRight: "8px"}} />
                                        Fetching in progress...
                                    </span>
                                ) : (
                                    getLastUpdatedText()
                                )}
                            </Typography>
                        </Grid>
                    )}

                    {/* Info Text */}
                    <Grid item size={12}>
                        <Alert severity="warning">
                            <Typography style={{
                                fontSize: "14px",
                                lineHeight: "1.5",
                                color: theme.palette.text.primary
                            }}>
                                Fetching data may take up to 30 minutes. Check back later to see your updated items. In some cases, you may need to refresh your browser.
                            </Typography>
                            <Typography style={{
                                fontSize: "14px",
                                lineHeight: "1.5",
                                color: theme.palette.text.primary,
                                marginTop: "12px"
                            }}>
                                Please note: You will not be able to click the refresh button while a job is running. To view the last update time for a sync, select the specified supplier and check the 'Last Updated' timestamp next to the supplier selector.
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions style={{padding: "0 12px 12px 12px"}}>
                <Grid container justifyContent={"flex-end"} spacing={1}>
                    <Grid item>
                        <Button
                            onClick={handleClose}
                            variant="text"
                            style={{
                                textTransform: "none",
                                minWidth: "80px"
                            }}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            onClick={handleTriggerSync}
                            variant="text"
                            color="primary"
                            disabled={isConfirmDisabled}
                            style={{
                                textTransform: "none",
                                minWidth: "120px"
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={16} style={{marginRight: "8px"}} />
                                    Triggering...
                                </>
                            ) : (
                                "Refresh Data"
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
            </Dialog>
            <EnhancedSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity="success"
                onClose={() => setSnackbarOpen(false)}
                autoHideDuration={6000}
            />
        </>
    );
}

export default RefreshSupplierDataModal;